import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PostGetLocationSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetLocationSearchRequest';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { SearchMockService } from '../../../shared/services/search-mock.service';
import { NeedHelpDecidingComponent} from '../../../../get-care-now-feature/mobile/components/need-help-deciding/need-help-deciding.component';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { LocationSearchFilters } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Component({
  selector: 'app-location-search-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, NeedHelpDecidingComponent, MatProgressSpinner],
  templateUrl: './location-search.component.html',
  styleUrl: './location-search.component.css'
})
export class LocationSearchComponent {
  mcAppGateway = inject(MCAppGatewayService);
  searchState = inject(SearchStateService);

  public title: string = 'Find a Location';
  public searchByText: string = 'Search for a facility or location';
  public namePlaceholderText: string = 'Facility or location name';
  public cityZipPlaceholderText: string = 'City or zip code (required)*'
  public fieldRequiredText: string = 'Location information required*';
  public searchBtnText: string = 'Search';
  public quickSearchTitleText: string = 'Quick searches:';
  public showSpinner: boolean = false;

  @Input() locale: string = '';
  @Input() isPlus: boolean | null = null;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() locationQuickSearchOptions: any[] = [];
  @Input() locationSearchFilters: LocationSearchFilters | null = null;
  @Input() locationTypeAheadOptions: string[] = [];
  @Input() cityZipTypeAheadOptions: string[] = []; 

  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  showSpinner$ = this.searchState.showSpinner$;

  filteredLocationOptions!: Observable<string[]>;
  filteredCityZipOptions!: Observable<string[]>;

  sortMethod: "distance" | "availability" | "name" = "distance";
  locationSearchForm = new FormGroup({
    facilityOrLocationName: new FormControl(''),
    cityOrZip: new FormControl('', Validators.required)
  });

  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
  }

  ngOnInit() {
    this.translate.use(sessionStorage.getItem('locale') || 'en-US');

    // Initialize autocomplete filtering
    this.filteredLocationOptions = this.locationSearchForm.get('facilityOrLocationName')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchValue = value || '';
        return searchValue.length < 3 ? [] : this._filterOptionsFuzzy(searchValue, this.locationTypeAheadOptions);
      })
    );

    this.filteredCityZipOptions = this.locationSearchForm.get('cityOrZip')!.valueChanges.pipe(
      startWith(''),
      map(value => {
        const searchValue = value || '';
        return searchValue.length < 3 ? [] : this._filterOptionsFuzzy(searchValue,  this.cityZipTypeAheadOptions);
      })
    );
    
    // Load translations
    this.translate.get('FINDALOCATION').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('LOCATION_SEARCH_BY').subscribe((translated: string) => {
      this.searchByText = translated;
    }); 

    this.translate.get('LOCATION_NAME_PLACEHOLDER').subscribe((translated: string) => {
      this.namePlaceholderText = translated;
    });
    
    this.translate.get('LOCATION_CITYZIP_PLACEHOLDER').subscribe((translated: string) => {
      this.cityZipPlaceholderText = translated;
    });

    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchBtnText = translated;
    });

    this.translate.get('QUICK_SEARCH_TITLE').subscribe((translated: string) => {  
      this.quickSearchTitleText = translated;
    });

    // Subscribe to search query to restore form values
    this.locationSearchQuery$.subscribe(query => {
      if (query) {
        this.locationSearchForm.patchValue({
          facilityOrLocationName: query.facilityOrLocationName,
          cityOrZip: query.cityOrZip
        });
      }
    }); 

    this.searchState.locationSortMethod$.subscribe( sortMethod => {
      if (sortMethod) {
        this.sortMethod = sortMethod
      }
    });
    
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });
  }

  onSubmit() {
    this.searchState.setShowSpinner(true);

    if (this.locationSearchForm.valid) {
      const formValues = this.locationSearchForm.value;

      if (formValues.cityOrZip) {
        var request = new PostGetLocationSearchRequest();
        request.facilityOrLocationName = formValues.facilityOrLocationName || undefined;
        request.cityOrZip = formValues.cityOrZip;

        this.locationSearchFilters?.serviceList.forEach(service => {
          if (formValues.facilityOrLocationName != null && service.value.toLowerCase() == (formValues.facilityOrLocationName.toLowerCase())) {
            request.services = service.value;
            request.facilityOrLocationName = "";
          }
        });

        this.locationSearchFilters?.typeList.forEach(type => {
          if (formValues.facilityOrLocationName != null && type.value.toLowerCase() == (formValues.facilityOrLocationName.toLowerCase())) {
            request.types = type.value;
            request.facilityOrLocationName = "";
          }
        });

        if (this.currentLocation != null) {
          request.latitude = this.currentLocation?.latitude;
          request.longitude = this.currentLocation?.longitude;
        }

        sessionStorage.setItem('locationSearchMethod', 'freeText');
        console.log('Form Submitted!', formValues, request);
        this.postGetLocationSearch(request);
      }
      else {
        alert('Please fill in all required fields before searching.');
        this.searchState.setShowSpinner(false);
      }
    }
    else {
      alert('Form is invalid. Please check the input fields.');
      console.log('Form is invalid', this.locationSearchForm.errors);
      this.searchState.setShowSpinner(false);
    }
  }

  onQuickSearch(option: any) {
    console.log('Quick Search Clicked!', option);
    this.searchState.setShowSpinner(true);

    var cityOrZip = this.locationSearchForm.get('cityOrZip')?.value ?? null;

    if (!cityOrZip) {
      console.warn('City or Zip is required for Quick Search.');
      this.searchState.setShowSpinner(false);
      return;
    }

    var request = new PostGetLocationSearchRequest();
    request.services = option.serviceList;
    request.types = option.typeList;
    request.cityOrZip = cityOrZip;

    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: '',
      cityOrZip: cityOrZip
    });

    if (this.currentLocation != null) {
      request.latitude = this.currentLocation?.latitude;
      request.longitude = this.currentLocation?.longitude;
    }

    sessionStorage.setItem('locationSearchMethod', 'quickSearch');
    console.log('Submitting Quick Search! ', request);
    this.postGetLocationSearch(request);
  }

  postGetLocationSearch(request: PostGetLocationSearchRequest) {
    
    this.mcAppGateway.PostGetLocationSearch(request).subscribe({
      next: (response) => {
        console.log('Location Search Response:', response);
        this.locationSearchForm.reset();

        var sortedResponse = this.searchState.sortLocationSearchResults(response || [], this.sortMethod);
        this.searchState.setLocationSearchResults(sortedResponse);

        if (request.facilityOrLocationName && this.locationSearchFilters != null) {
          this.locationSearchFilters.facilityOrLocationName = request.facilityOrLocationName;
        }

        if (request.services) {
          this.locationSearchFilters?.serviceList?.forEach(service => {
            if (request.services?.toLowerCase().includes(service.value.toLowerCase())) {
              service.isSelected = true;
            }
          });
        }

        if (request.types) {
          this.locationSearchFilters?.typeList?.forEach(type => {
            if (request.types?.toLowerCase().includes(type.value.toLowerCase())) {
              type.isSelected = true;
            }
          });
        }

        if (request.cityOrZip != null && request.cityOrZip != '' && this.locationSearchFilters != null) {
          this.locationSearchFilters.location = request.cityOrZip;
        }
        
        if(this.locationSearchFilters)
        this.searchState.setLocationSearchFilters(this.locationSearchFilters);

        // Set the search query in state service for display purposes
        this.searchState.setLocationSearchQuery({
          facilityOrLocationName: request.facilityOrLocationName ?? '',
          cityOrZip: request.cityOrZip ?? '',
        });

        this.searchState.setShowSpinner(false);
      },
      error: (error) => {
        // Clear previous query & results
        this.searchState.setLocationSearchQuery({facilityOrLocationName: '', cityOrZip: ''});
        this.searchState.clearLocationSearchResults();

        console.error('Location Search Error:', error);
        alert('An error occurred while searching. Please try again.');
        
        this.searchState.setShowSpinner(false);
      }
    });
  }

  private _fuzzyMatch(filterValue: string, option: string): boolean {

    // Remove spaces and convert to lowercase for comparison
    const normalizedFilter = filterValue.toLowerCase().replace(/\s/g, '');
    const normalizedOption = option.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9]/g, '');
    
    return normalizedOption.includes(normalizedFilter)
  }

  private _filterOptionsFuzzy(value: string, options: string[]): string[] {
    if (!value) return options;
    
    return options.filter(option => 
      this._fuzzyMatch(value, option) || option.toLowerCase().includes(value.toLowerCase())
    );
  }
}
